// SimpleGoogleMap.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useSimpleFormContext } from './SimpleFormProvider';
import useFormElement from '../../hooks/useFormElement';
import { useEffect, useState, useRef } from 'react';
import { GoogleMap, LoadScript } from '@react-google-maps/api';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { REACT_APP_GOOGLE_API_KEY: GOOGLE_API_KEY, REACT_APP_GOOGLE_API_MAP_ID: MAP_ID } =
  process.env;
const GOOGLE_MAP_LIBRARIES = ['marker']; // ← constante fixa

// *******************************************************************************
// FUNCTIONS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleGoogleMap = ({
  name = 'addressCoordinates',
  subdocumentField,
  lat = -22.9110238,
  lng = -47.09600229999999,
  zoom = 16,
  style = { width: '100%', height: 200 },
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { form, foundZip } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { fullFieldName: coordinatesFullField } = useFormElement(name, subdocumentField);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [libsLoaded, setLibsLoaded] = useState(false); // ← flag de controle

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const initialMapProps = {
    center: {
      lat: lat,
      lng: lng,
    },
    zoom,
    style,
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [mapProps, setMapProps] = useState(initialMapProps);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const coordinates = form?.getFieldValue(coordinatesFullField) || foundZip?.addressCoordinates;
    const center = {
      lat: coordinates?.[0] || initialMapProps.center.lat,
      lng: coordinates?.[1] || initialMapProps.center.lng,
    };

    setMapProps(prevStates => ({ ...prevStates, center }));
  }, [form, foundZip]);

  // Create AdvancedMarkerElement when map and libraries are ready
  useEffect(() => {
    if (
      libsLoaded &&
      window.google?.maps?.marker?.AdvancedMarkerElement &&
      mapRef.current &&
      mapProps?.center?.lat &&
      mapProps?.center?.lng
    ) {
      // Remove existing marker if any
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }

      // Create new AdvancedMarkerElement
      const { AdvancedMarkerElement } = window.google.maps.marker;
      const marker = new AdvancedMarkerElement({
        map: mapRef.current,
        position: mapProps.center,
      });

      markerRef.current = marker;
    }
  }, [libsLoaded, mapProps.center?.lat, mapProps.center?.lng]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <LoadScript
      googleMapsApiKey={GOOGLE_API_KEY}
      mapIds={[MAP_ID]} // ← Adiciona o Map ID aqui
      libraries={GOOGLE_MAP_LIBRARIES}
      onLoad={() => {
        setLibsLoaded(true);
      }}
    >
      <GoogleMap
        mapContainerStyle={mapProps.style}
        center={mapProps.center}
        zoom={mapProps.zoom}
        onLoad={mapInstance => {
          mapRef.current = mapInstance;
        }}
      />
    </LoadScript>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleGoogleMap;
